import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ArrowUp, FolderTree, Loader2, MessageCircleQuestion, MessageSquare, Paperclip, Square, SquareTerminal, Upload } from 'lucide-react'
import type { ChatClientMessage, ChatConversation, ChatItem, ChatQuestion, ChatServerMessage } from '@one-deployment/protocol'
import type { AgentSession } from '@one-deployment/api'
import { EmptyState } from '@/components/status'
import { ResizeHandle } from '@/components/ResizeHandle'
import { Segmented } from '@/components/ui'
import { useMe } from '@/features/auth/useMe'
import { openBoxSocket, ReconnectingBoxSocket, type BoxSocketState } from '@/lib/box'
import { terminalMode } from '@/lib/can'
import { cn } from '@/lib/cn'
import { DRAG_PATH, mention, useTermInsert } from '@/lib/files'
import { Badge } from '@/components/shadcn/badge'
import { Button } from '@/components/shadcn/button'
import { Card } from '@/components/shadcn/card'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Input } from '@/components/shadcn/input'
import { RadioGroup, RadioGroupItem } from '@/components/shadcn/radio-group'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { AttachmentTray } from './chat/AttachmentTray'
import { useAttachments, type Attachment } from './chat/attachments'
import { LinkEditor, type EditorHandle } from './chat/LinkEditor'
import { SessionCtx, Transcript, type MessageActions } from './chat/Transcript'
import { StreamTranscript } from './chat/StreamTranscript'
import { answerOf, feed, liveStatus, streamWorking, type BoxStatus } from './chat/stream'
import { LiveStatus } from './chat/LiveStatus'
import type { Timeline } from '@one-deployment/claude-stream'
import { ScreenCard } from './chat/ScreenCard'
import { ConversationBar } from './chat/Conversations'
import { useClaudeKit, type ClaudeKit } from './chat/claudeKit'
import { KitSettings } from './chat/KitSettings'
import { ModelPicker } from './chat/ModelPicker'
import { PermissionPicker } from './chat/PermissionPicker'
import { setPermissionMode } from '@one-deployment/claude-stream'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/shadcn/hover-card'
import { matchSlash, SlashMenu, slashOptions, SlashPreview, type SlashOption } from './chat/SlashMenu'
import { useConversations } from '@/stores/conversations'
import { FilePanel } from './FilePanel'
import { TerminalPane, type TermStatus } from './Terminal'
import { FleetNotice } from '@/features/servers/FleetNotice'

/**
 * A session the way Claude's desktop app shows one: the conversation, tool
 * calls as cards, questions as forms. Underneath it is the same tmux session
 * (the box reads its transcript and types what you write), so the terminal
 * is one click away and both can be open at once.
 */
const VIEW_KEY = 'trisof.session.view'
const FILES_KEY = 'trisof.files.panel'
const FILES_WIDTH_KEY = 'trisof.files.width'

type Props = { session: AgentSession | undefined; project: string; run?: boolean; bare?: boolean; onStatus?: (s: TermStatus) => void }

/** Chat or terminal, the choice kept per browser. The dev server's run window is only ever a terminal. */
export function SessionPane(props: Props) {
  const [view, setView] = useState<'chat' | 'terminal'>(() => (localStorage.getItem(VIEW_KEY) === 'terminal' ? 'terminal' : 'chat'))
  if (props.run) return <TerminalPane {...props} />
  const pick = (v: 'chat' | 'terminal') => { setView(v); localStorage.setItem(VIEW_KEY, v) }
  const toggle = (
    <Segmented value={view} onChange={pick} options={[
      { value: 'chat', label: <span className="flex items-center gap-1"><MessageSquare size={12} />Chat</span> },
      { value: 'terminal', label: <span className="flex items-center gap-1"><SquareTerminal size={12} />Terminal</span> },
    ]} />
  )
  return view === 'chat'
    ? <ChatPane {...props} toggle={toggle} />
    : <TerminalView {...props} toggle={toggle} />
}

const TERM_KEY = 'trisof.session.terminal'

/**
 * The Terminal tab: a bash in the session's worktree (ONE-11), or Claude's
 * own pane. A headless session's pane is only its wrapper's log, so people
 * wanting a terminal got nothing to type into.
 */
function TerminalView({ toggle, ...props }: Props & { toggle: ReactNode }) {
  const [which, setWhich] = useState<'shell' | 'claude'>(() => (localStorage.getItem(TERM_KEY) === 'claude' ? 'claude' : 'shell'))
  const pick = (v: 'shell' | 'claude') => { setWhich(v); localStorage.setItem(TERM_KEY, v) }
  const actions = (
    <div className="flex items-center gap-2">
      <Segmented value={which} onChange={pick} options={[{ value: 'shell', label: 'Shell' }, { value: 'claude', label: 'Claude' }]} />
      {toggle}
    </div>
  )
  return (
    <div className="flex h-full min-h-0 flex-col">
      {props.bare && <div className="flex h-8 shrink-0 items-center justify-end border-b border-border px-2">{actions}</div>}
      <div className="min-h-0 flex-1"><TerminalPane key={which} {...props} shell={which === 'shell'} actions={actions} /></div>
    </div>
  )
}

/** `bare` (the session page's chat window) drops the status row and the file panel: the window's header and the Files window have them. */
export function ChatPane({ session, project, bare = false, onStatus, toggle }: Props & { toggle?: ReactNode }) {
  const me = useMe().data
  const mode = terminalMode(me, project)
  const [state, setState] = useState<BoxSocketState>('connecting')
  const [viewers, setViewers] = useState(0)
  const [items, setItems] = useState<ChatItem[]>([])
  // A headless session (workflow.chat.stream) sends Claude Code's own stream instead of items.
  const tlRef = useRef<Timeline | null>(null)
  const [, setTick] = useState(0)
  const tl = tlRef.current
  const [questions, setQuestions] = useState<{ id: string; questions: ChatQuestion[] }[]>([])
  // What the terminal shows when it waits on a key the transcript cannot (a dialog, a menu).
  const [screen, setScreen] = useState<string | null>(null)
  // The box's live status (ONE-9): branch, and the TUI footer in terminal mode.
  const [boxStatus, setBoxStatus] = useState<BoxStatus | null>(null)
  const [typed, setTyped] = useState(false)
  const [stoppedAt, setStoppedAt] = useState<number | null>(null)
  // What you sent, shown at once until the session echoes it back. `seen` is
  // how many identical messages were already there, so a repeat still waits.
  const [conversations, setConversations] = useState<ChatConversation[] | null>(null)
  const [sent, setSent] = useState<{ id: string; text: string; seen: number; after: number }[]>([])
  const sendRef = useRef<(m: ChatClientMessage) => void>(() => {})
  const [files, setFiles] = useState(() => localStorage.getItem(FILES_KEY) !== 'hidden')
  const [filesWidth, setFilesWidth] = useState(() => Number(localStorage.getItem(FILES_WIDTH_KEY)) || 240)
  const row = useRef<HTMLDivElement>(null)
  const editor = useRef<EditorHandle>(null)
  const atts = useAttachments(session?.id)
  const kit = useClaudeKit(session?.id, mode === 'rw' ? 'rw' : 'ro')
  const [dragOver, setDragOver] = useState<null | { files: number; path: boolean }>(null)
  const dragDepth = useRef(0)
  // Opening a session puts the cursor where you type, and again once it
  // connects (the editor is disabled until then).
  const ready = state === 'open'
  useEffect(() => { const t = setTimeout(() => editor.current?.focus(), 50); return () => clearTimeout(t) }, [session?.id, ready])
  const showFiles = (on: boolean) => { setFiles(on); localStorage.setItem(FILES_KEY, on ? 'shown' : 'hidden') }

  useEffect(() => {
    if (!session) return
    setItems([]); setQuestions([]); setSent([]); setStoppedAt(null); setConversations(null); setScreen(null); setBoxStatus(null); tlRef.current = null
    const sock = new ReconnectingBoxSocket(() => openBoxSocket({ sessionId: session.id }, 'chat', mode), {
      onMessage: (data) => {
        if (typeof data !== 'string') return
        const m = JSON.parse(data) as ChatServerMessage
        if (m.type === 'items') {
          setItems((prev) => {
            if (m.reset) return m.items
            // A replay after a reconnect repeats ids; keep one of each.
            const seen = new Set(prev.map((i) => i.id))
            const add = m.items.filter((i) => !seen.has(i.id))
            return add.length ? [...prev, ...add] : prev
          })
        } else if (m.type === 'stream') {
          tlRef.current = feed(tlRef.current, m); setTick((n) => n + 1)
        } else if (m.type === 'question') setQuestions((q) => (q.some((x) => x.id === m.id) ? q : [...q, { id: m.id, questions: m.questions }]))
        else if (m.type === 'question_closed') setQuestions((q) => q.filter((x) => x.id !== m.id))
        else if (m.type === 'screen') setScreen(m.text ?? null)
        else if (m.type === 'status') setBoxStatus(m)
        else if (m.type === 'conversations') setConversations(m.items)
        else if (m.type === 'viewers') setViewers(m.count)
        else if (m.type === 'exit') setState('ended')
      },
      onOpen: () => { setQuestions([]); sock.send(JSON.stringify({ type: 'conversations' } satisfies ChatClientMessage)) },
      onState: (s) => setState(s),
      isFinal: (code) => code === 4000,
    })
    sock.start()
    sendRef.current = (m) => sock.send(JSON.stringify(m))
    const ping = setInterval(() => sendRef.current({ type: 'ping' }), 25_000)
    return () => { clearInterval(ping); sock.stop() }
  }, [session?.id, mode])

  const refreshConversations = () => sendRef.current({ type: 'conversations' })
  const switchConversation = (id?: string) => { setSent([]); setQuestions([]); sendRef.current(id ? { type: 'switch', id } : { type: 'switch' }) }
  // The session page shows the picker in the window's tab header instead.
  const publish = useConversations((s) => s.set)
  const drop = useConversations((s) => s.drop)
  const canSwitch = mode === 'rw' && state === 'open'
  useEffect(() => {
    if (!bare || !session) return
    publish(session.id, { items: conversations ?? [], answered: conversations !== null, canSwitch,
      refresh: () => sendRef.current({ type: 'conversations' }),
      switchTo: (id) => { setSent([]); setQuestions([]); sendRef.current(id ? { type: 'switch', id } : { type: 'switch' }) } })
  }, [bare, session, conversations, canSwitch, publish])
  useEffect(() => { const id = session?.id; return () => { if (bare && id) drop(id) } }, [bare, session?.id, drop])

  useEffect(() => { onStatus?.({ state, viewers, readOnly: mode === 'ro' }) }, [state, viewers, mode, onStatus])

  // Text from the file panel (an `@path`) lands in the message being written.
  const pending = useTermInsert((s) => s.next)
  useEffect(() => { if (pending && pending.sessionId === session?.id) editor.current?.insert(pending.text) }, [pending, session?.id])

  const userCount = (text: string) => items.filter((i) => i.kind === 'user' && i.text?.trim() === text.trim()).length
  const type = (text: string) => {
    sendRef.current({ type: 'send', text }); setTyped(true); setStoppedAt(null)
    // The stream echoes a message back as soon as Claude takes it.
    if (tlRef.current) return
    setSent((s) => [...s, { id: `local-${Date.now()}-${s.length}`, text, seen: userCount(text), after: items.length }])
  }
  // Written while Claude works: kept here, not typed in, so it can still be
  // cancelled; the first goes in when the turn ends (or on Send now).
  const [held, setHeld] = useState<{ id: string; text: string }[]>([])
  useEffect(() => { setHeld([]) }, [session?.id])
  const busy = tl ? streamWorking(tl) : sent.length > 0 || isWorking(items)
  const send = (text: string) => {
    // Written while Claude works: held here (both modes) so it can still be
    // cancelled or sent at once; the same words twice in a row are one message (ONE-6).
    if (busy) setHeld((h) => (h.at(-1)?.text === text ? h : [...h, { id: `held-${Date.now()}-${h.length}`, text }]))
    else type(text)
  }
  useEffect(() => {
    if (busy || state !== 'open' || !held.length) return
    const [first, ...rest] = held
    setHeld(rest); type(first!.text)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, state, held])
  const keys = useRef<ReturnType<typeof setTimeout>[]>([])
  useEffect(() => () => keys.current.forEach(clearTimeout), [])
  // Claude Code's own rewind: Esc twice opens its list of your messages with
  // the cursor below the last; Up once per message from the end reaches this
  // one. Its confirm menu then shows here (the screen card), so nothing is
  // removed until you choose there.
  const rewind = (id: string) => {
    const at = items.findIndex((i) => i.id === id)
    const back = items.slice(at).filter((i) => i.kind === 'user').length
    const seq: ChatClientMessage[] = [{ type: 'key', key: 'Escape' }, { type: 'key', key: 'Escape' },
      ...Array.from({ length: back }, () => ({ type: 'key', key: 'Up' }) as ChatClientMessage), { type: 'key', key: 'Enter' }]
    const gap = [0, 150, 900]
    let t = 0
    keys.current = seq.map((m, i) => { t += gap[i] ?? 120; return setTimeout(() => sendRef.current(m), t) })
  }
  // Stream mode: Claude restarts on the conversation cut before that message (--resume-session-at).
  const rewindStream = (b: { id: string; prev?: string | null }) =>
    sendRef.current(b.prev ? { type: 'rewind', drop: b.id, at: b.prev } : { type: 'rewind', drop: b.id })
  const actions: MessageActions = {
    rewind: mode === 'rw' && state === 'open' && !busy && !held.length ? (tl ? undefined : rewind) : undefined,
    rewindStream: mode === 'rw' && state === 'open' && !busy && !held.length && tl ? rewindStream : undefined,
    cancel: (id) => setHeld((h) => h.filter((x) => x.id !== id)),
    sendNow: (id) => { const m = held.find((x) => x.id === id); if (!m) return; setHeld((h) => h.filter((x) => x.id !== id)); type(m.text) },
  }
  useEffect(() => {
    setSent((s) => {
      // Echoed back word for word, or any new user turn if the session reworded it.
      const left = s.filter((o) => items.filter((i) => i.kind === 'user' && i.text?.trim() === o.text.trim()).length <= o.seen
        && !items.slice(o.after).some((i) => i.kind === 'user'))
      return left.length === s.length ? s : left
    })
  }, [items])
  // Stop shows at once; if Claude is somehow still going 5s later, Working comes back.
  // Stop shows at once: Working hides from the press until Claude writes
  // something new (its "Stopped" line ends the turn for good; anything else
  // means it is still going, and Working comes back).
  const seen = tl ? tl.blocks.length : items.length
  const working = busy && !(stoppedAt !== null && seen <= stoppedAt + 1 && !isWorkingAfter(items, stoppedAt))

  if (!session) return <EmptyState title="No session open" body="Pick a session, or start a new one." />

  return (
    <div className="flex h-full min-h-0 flex-col">
      {!bare && <div className="flex h-8 shrink-0 items-center gap-3 border-b border-border px-3 text-xs text-fg-subtle">
        <span title={state} className={state === 'open' ? 'text-ok' : state === 'unreachable' || state === 'ended' ? 'text-err' : 'text-warn'}>●{state !== 'open' && state !== 'connecting' && state !== 'reconnecting' && ` ${state}`}</span>
        <span>{viewers} watching</span>
        {mode === 'ro' && <span>read-only</span>}
        <span className="font-mono text-fg-faint">{session.tmuxSession}</span>
        <span className="ml-auto">{toggle}</span>
      </div>}
      <FleetNotice unreachable={state === 'unreachable'} />
      {typed && session.jobId && state === 'open' && <div className="flex items-center gap-3 border-b border-warn/30 bg-warn/10 px-3 py-1.5 text-xs">
        <span>You have the session. Claude waits, and carries on after 10 quiet minutes.</span>
        <Button variant="outline" size="xs" className="ml-auto" onClick={() => { sendRef.current({ type: 'resume' }); setTyped(false) }}>Resume now</Button>
      </div>}
      {state === 'ended' && <div className="border-b border-border bg-surface-2 px-3 py-1.5 text-xs text-fg-muted">This session has ended.</div>}
      <div ref={row} className="flex min-h-0 flex-1">
        {!bare && files && <>
          <div style={{ width: filesWidth }} className="h-full min-h-0 shrink-0 overflow-hidden"><FilePanel sessionId={session.id} mode={mode} onHide={() => showFiles(false)} /></div>
          <ResizeHandle label="Resize the file panel" onResize={(x) => {
            const w = Math.min(600, Math.max(160, x - (row.current?.getBoundingClientRect().left ?? 0)))
            setFilesWidth(w); localStorage.setItem(FILES_WIDTH_KEY, String(w))
          }} />
        </>}
        {!bare && !files && <Button variant="ghost" size="icon-xs" title="Show files" aria-label="Show files" onClick={() => showFiles(true)}
          className="h-auto shrink-0 items-start rounded-none border-r px-1 pt-1.5 text-fg-faint"><FolderTree size={13} /></Button>}
        {/* The whole conversation takes a drop, not just the box you type in. */}
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-bg"
          onDragEnter={(e) => {
            const t = e.dataTransfer.types
            if (mode !== 'rw' || !(t.includes('Files') || t.includes(DRAG_PATH))) return
            e.preventDefault(); dragDepth.current++
            setDragOver({ files: [...e.dataTransfer.items].filter((i) => i.kind === 'file').length, path: t.includes(DRAG_PATH) })
          }}
          onDragOver={(e) => { if (dragOver) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' } }}
          onDragLeave={() => { if (--dragDepth.current <= 0) { dragDepth.current = 0; setDragOver(null) } }}
          onDrop={(e) => {
            if (!dragOver) return
            e.preventDefault(); dragDepth.current = 0; setDragOver(null)
            const p = e.dataTransfer.getData(DRAG_PATH)
            if (p) editor.current?.insert(mention(p))
            else if (e.dataTransfer.files.length) atts.add([...e.dataTransfer.files])
            editor.current?.focus()
          }}>
          {dragOver && <DropOverlay files={dragOver.files} path={dragOver.path} />}
          {!bare && <ConversationBar items={conversations ?? []} answered={conversations !== null} canSwitch={mode === 'rw' && state === 'open'}
            onOpen={refreshConversations} onSwitch={switchConversation} />}
          <SessionCtx.Provider value={session.id}>{tl
            ? <StreamTranscript tl={tl} working={working && state === 'open'} readOnly={mode !== 'rw'} held={held} actions={actions} live={<LiveStatus s={liveStatus(tl, boxStatus)} />}
                onAnswer={(p, a) => sendRef.current({ type: 'control', message: answerOf(p, a) })}
                question={(qs, answer) => <QuestionCard questions={qs} readOnly={mode !== 'rw'} onAnswer={answer} />} />
            : <Transcript key={session.id} items={items} sent={sent} held={held} actions={actions} live={<LiveStatus s={liveStatus(null, boxStatus)} />} working={working && state === 'open' && !screen} loading={state === 'connecting' && !items.length} />}</SessionCtx.Provider>
          {questions.filter((q) => q.questions.length && !answeredIn(items, q.questions) && !(tl && streamAsks(tl, q.questions))).map((q) => (
            <QuestionCard key={q.id} questions={q.questions} readOnly={mode !== 'rw'}
              onAnswer={(answers) => { sendRef.current({ type: 'answer', id: q.id, answers }); setQuestions((all) => all.filter((x) => x.id !== q.id)) }} />
          ))}
          {/* Claude's limit wait screen is said in the chat (LimitNotice), not as a terminal to answer. */}
          {screen && state === 'open' && !/usage limit reached/i.test(screen) && <ScreenCard text={screen} readOnly={mode !== 'rw'} onKey={(key) => sendRef.current({ type: 'key', key })} />}
          {mode === 'rw'
            ? <Composer editor={editor} atts={atts} kit={kit} project={project} working={working} disabled={state !== 'open'}
                offline={state === 'ended' ? 'This session has ended.' : state === 'unreachable' ? 'Cannot reach the server. Still retrying…' : undefined}
                permission={<PermissionPicker mode={tl?.session.permissionMode ?? boxStatus?.permission_mode ?? null} disabled={state !== 'open'}
                  onPick={tl ? (m) => sendRef.current({ type: 'control', message: setPermissionMode(`perm-${Date.now()}`, m) }) : undefined}
                  onCycle={() => sendRef.current({ type: 'key', key: 'BTab' })} />}
                onSend={send} onStop={() => { sendRef.current({ type: 'interrupt' }); setTyped(true); setSent([]); setStoppedAt(seen) }} />
            : <div className="border-t border-border px-4 py-2 text-xs text-fg-faint">Read-only: you can watch, not write.</div>}
        </div>
      </div>
    </div>
  )
}

/** Claude is mid-turn when the newest thing is not its own text: a message it has not answered, or a tool still running. */
/** Claude did real work after the Stop at `at` (not just its own "Stopped" line). */
function isWorkingAfter(items: ChatItem[], at: number): boolean {
  return items.slice(at).some((i) => i.kind !== 'user' && i.kind !== 'thinking')
}

export function isWorking(items: ChatItem[]): boolean {
  for (let i = items.length - 1; i >= 0; i--) {
    const it = items[i]!
    if (it.kind === 'thinking') continue
    // Stopped: Claude records the interrupt as a user line, and waits.
    if (it.kind === 'user' && /^\[Request interrupted/.test(it.text ?? '')) return false
    if (it.kind === 'tool_result' && /^\[Request interrupted/.test(it.text ?? '')) return false
    // A refused tool ends the turn too: Claude stops and waits to be told how to go on (ONE-8).
    if (it.kind === 'tool_result' && it.is_error && /^The user doesn't want to (proceed|take this action)/.test(it.text ?? '')) return false
    return it.kind !== 'assistant'
  }
  return false
}

/**
 * A question the transcript already shows closed (answered, refused or
 * interrupted in the terminal): its card would wait for nobody (ONE-7).
 */
export function answeredIn(items: ChatItem[], qs: ChatQuestion[]): boolean {
  const done = new Set(items.filter((i) => i.kind === 'tool_result').map((i) => i.tool_use_id))
  return items.some((i) => i.kind === 'tool_use' && i.tool === 'AskUserQuestion' && done.has(i.tool_use_id)
    && ((i.input as { questions?: ChatQuestion[] } | undefined)?.questions?.[0]?.question === qs[0]?.question))
}

/** Stream mode: the question is already a card in the conversation, or its call has ended. */
function streamAsks(tl: Timeline, qs: ChatQuestion[]): boolean {
  const first = qs[0]?.question
  const asks = (input: Record<string, unknown>) => (input.questions as ChatQuestion[] | undefined)?.[0]?.question === first
  const walk = (bs: Timeline['blocks']): boolean => bs.some((b) =>
    (b.kind === 'permission' && b.tool === 'AskUserQuestion' && asks(b.input))
    || (b.kind === 'tool' && ((b.name === 'AskUserQuestion' && b.status !== 'running' && b.status !== 'streaming' && asks(b.input)) || walk(b.children))))
  return walk(tl.blocks)
}

const OTHER = '__other__'

/** One pick is a shadcn RadioGroup (arrow keys move between options); several are plain checkboxes. */
function OptionGroup({ multi, value, onPick, children }: { multi: boolean; value: string; onPick: (label: string) => void; children: ReactNode }) {
  if (multi) return <div className="flex flex-col gap-2">{children}</div>
  return <RadioGroup value={value} onValueChange={onPick} className="gap-2">{children}</RadioGroup>
}

/**
 * Claude's open question as a form: options to pick (one, or several when
 * the question allows), or an answer in your own words. The first answer
 * from anyone watching is the one Claude gets.
 */
function QuestionCard({ questions, readOnly, onAnswer }: { questions: ChatQuestion[]; readOnly: boolean; onAnswer: (a: Record<string, string>) => void }) {
  const [picked, setPicked] = useState<Record<number, string[]>>({})
  const [other, setOther] = useState<Record<number, string>>({})
  const answerOf = (i: number) => {
    const p = picked[i] ?? []
    return p.map((l) => (l === OTHER ? (other[i] ?? '').trim() : l)).filter(Boolean).join(', ')
  }
  const ready = questions.every((_, i) => answerOf(i))
  const toggle = (i: number, label: string, multi: boolean) => setPicked((all) => {
    const cur = all[i] ?? []
    return { ...all, [i]: multi ? (cur.includes(label) ? cur.filter((l) => l !== label) : [...cur, label]) : [label] }
  })
  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-3">
      <Card className="gap-0 overflow-hidden rounded-lg py-0 shadow-[0_10px_30px_-18px_rgb(0_0_0/0.8)]">
        <div className="flex items-center gap-2 border-b border-border bg-accent/5 px-4 py-2.5 text-xs font-medium text-accent"><MessageCircleQuestion size={14} />Claude needs an answer</div>
        <div className="flex max-h-[50vh] flex-col gap-4 overflow-y-auto p-4">
          {questions.map((q, i) => {
            const multi = !!q.multiSelect
            const cur = picked[i] ?? []
            return (
              <fieldset key={i} className="flex flex-col gap-2" disabled={readOnly}>
                <legend className="mb-1 text-sm font-medium">
                  {q.header && <Badge variant="secondary" className="mr-2 font-normal text-fg-subtle">{q.header}</Badge>}
                  {q.question}
                  {multi && <span className="ml-2 text-xs font-normal text-fg-faint">pick any</span>}
                </legend>
                <OptionGroup multi={multi} value={cur[0] ?? ''} onPick={(l) => toggle(i, l, false)}>
                {[...q.options, { label: OTHER, description: undefined }].map((o) => {
                  const on = cur.includes(o.label)
                  return (
                    <label key={o.label} className={cn('flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm transition-colors has-[:focus-visible]:border-ring has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-ring/30', on ? 'border-accent/40 bg-accent/5' : 'border-border hover:border-fg-faint/40 hover:bg-hover')}>
                      {multi
                        ? <Checkbox checked={on} onCheckedChange={() => toggle(i, o.label, multi)} className="mt-0.5" />
                        : <RadioGroupItem value={o.label} className="mt-0.5" />}
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        {o.label === OTHER ? (
                          on
                            ? <Input autoFocus value={other[i] ?? ''} onChange={(e) => setOther((x) => ({ ...x, [i]: e.target.value }))} onClick={(e) => e.preventDefault()} placeholder="Your answer" className="h-8 bg-bg text-sm md:text-sm" />
                            : <span className="text-fg-muted">Other…</span>
                        ) : <span>{o.label}</span>}
                        {o.description && <span className="text-xs text-fg-subtle">{o.description}</span>}
                      </span>
                    </label>
                  )
                })}
                </OptionGroup>
              </fieldset>
            )
          })}
        </div>
        {!readOnly && <div className="flex justify-end border-t border-border px-4 py-2">
          <Button size="sm" disabled={!ready} onClick={() => onAnswer(Object.fromEntries(questions.map((q, i) => [q.question, answerOf(i)])))}>Send answer</Button>
        </div>}
      </Card>
    </div>
  )
}

function DropOverlay({ files, path }: { files: number; path: boolean }) {
  return (
    <div className="pointer-events-none absolute inset-2 z-20 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-accent bg-accent/10 backdrop-blur-[2px] animate-in fade-in-0 zoom-in-[0.98]">
      <div className="flex size-12 items-center justify-center rounded-full bg-accent text-white shadow-lg"><Upload size={22} /></div>
      <div className="text-sm font-medium">{path ? 'Drop to reference this file' : files > 1 ? `Drop ${files} files to attach` : 'Drop to attach'}</div>
      {!path && <div className="text-xs text-fg-subtle">Any document, image or data file · up to 100 MB each</div>}
    </div>
  )
}

function Composer({ editor, atts, kit, project, working, disabled, offline, permission, onSend, onStop }: {
  editor: React.RefObject<EditorHandle | null>; atts: ReturnType<typeof useAttachments>; kit: ClaudeKit; project: string; working: boolean; disabled: boolean; offline?: string
  permission?: ReactNode
  onSend: (text: string) => void; onStop: () => void
}) {
  const [text, setText] = useState('')
  // `/word` at the start, nothing after it yet: the command menu is open.
  const slash = /^\/([\w:-]*)$/.exec(text.trim())
  const [menuClosed, setMenuClosed] = useState(false)
  const [active, setActive] = useState(0)
  const all = useMemo(() => slashOptions(kit.items), [kit.items])
  const matches = slash && !menuClosed ? matchSlash(all, slash[1] ?? '') : []
  useEffect(() => { setActive(0) }, [slash?.[1]])
  // The command the message starts with, once picked or typed in full: named above the box, its preview on hover.
  const chosen = useMemo(() => { const n = /^\/([\w:-]+)(\s|$)/.exec(text.trim())?.[1]; return matches.length ? undefined : all.find((o) => o.name === n) }, [text, all, matches.length])
  useEffect(() => { if (!text.trim().startsWith('/')) setMenuClosed(false) }, [text])
  const pickCommand = (o: SlashOption) => { editor.current?.clear(); editor.current?.insert(`/${o.name} `); editor.current?.focus(); setMenuClosed(true) }
  const onKey = (e: React.KeyboardEvent) => {
    if (!matches.length) return false
    if (e.key === 'ArrowDown') { setActive((a) => (a + 1) % matches.length); return true }
    if (e.key === 'ArrowUp') { setActive((a) => (a - 1 + matches.length) % matches.length); return true }
    if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
      const o = matches[active]
      // Typed in full already: Enter sends it rather than picking it again.
      if (e.key === 'Enter' && o && slash?.[1] === o.name) return false
      if (o) pickCommand(o)
      return true
    }
    if (e.key === 'Escape') { setMenuClosed(true); return true }
    return false
  }
  const [sending, setSending] = useState(false)
  const pick = useRef<HTMLInputElement>(null)
  const failed = atts.items.some((a: Attachment) => a.status === 'error')
  const uploading = atts.items.some((a: Attachment) => a.status === 'uploading')
  const canSend = (!!text.trim() || atts.items.length > 0) && !disabled && !failed && !sending

  const submit = async () => {
    if (!canSend) return
    const t = text.trim()
    const ids = atts.items.map((a) => a.id)
    setSending(true)
    // Uploads started on drop; usually they are done by now and this is instant.
    const paths = ids.length ? await atts.settle(ids) : []
    setSending(false)
    if (!paths) return
    onSend(paths.map(mention).join('') + t)
    editor.current?.clear()
    atts.clear()
  }

  const hint = failed ? 'A file did not upload. Click it to retry, or remove it.'
    : sending ? 'Sending once the files finish uploading…'
    : disabled ? (offline ?? '')
    : ''

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-4">
      <div data-slot="input-group" className="relative rounded-lg border border-input bg-surface-2 outline-none transition-[color,box-shadow,border-color] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/40">
        <SlashMenu options={matches} active={active} onPick={pickCommand} onHover={setActive} />
        {chosen && (
          <HoverCard openDelay={150} closeDelay={80}>
            <HoverCardTrigger asChild>
              <div className="mx-3 mt-2 flex min-w-0 cursor-default items-baseline gap-2 rounded-md border bg-surface-3/60 px-2 py-1 text-[11px]">
                <span className="shrink-0 font-mono text-foreground">/{chosen.name}</span>
                <span className="min-w-0 flex-1 truncate text-muted-foreground">{chosen.description}</span>
                <span className="shrink-0 text-[10px] text-fg-faint">{chosen.source}</span>
              </div>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="start" className="w-96"><SlashPreview o={chosen} /></HoverCardContent>
          </HoverCard>
        )}
        <AttachmentTray items={atts.items} onRemove={atts.remove} onRetry={atts.retry} />
        <LinkEditor ref={editor} placeholder={working ? 'Claude is working. Write to steer it…' : 'Reply to Claude…'}
          onChange={setText} onSubmit={() => void submit()} onEscape={working ? onStop : undefined} onPasteFiles={atts.add} onKey={onKey} />
        <div className="flex items-center gap-2 px-2 pb-2 text-[11px] text-fg-faint">
          <input ref={pick} type="file" multiple hidden onChange={(e) => { if (e.target.files?.length) atts.add([...e.target.files]); e.target.value = '' }} />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-sm" className="size-7 rounded-full text-fg-subtle" aria-label="Attach files" onClick={() => pick.current?.click()}><Paperclip /></Button>
            </TooltipTrigger>
            <TooltipContent>Attach files</TooltipContent>
          </Tooltip>
          <KitSettings kit={kit} />
          <span className={cn('min-w-0 flex-1 truncate', (failed || (disabled && !sending)) && 'text-warn')}>{hint}</span>
          {working && <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon-sm" className="size-7 rounded-full" aria-label="Stop" onClick={onStop}><Square className="size-2.5" fill="currentColor" /></Button>
            </TooltipTrigger>
            <TooltipContent>Stop (Esc)</TooltipContent>
          </Tooltip>}
          {permission}
          <ModelPicker project={project} disabled={disabled} onCommand={onSend} />
          <Button size="icon-sm" className="size-7 rounded-full" aria-label="Send" title={uploading ? 'Sends when uploads finish' : 'Send'} disabled={!canSend} onClick={() => void submit()}>
            {sending ? <Loader2 className="animate-spin" /> : <ArrowUp />}
          </Button>
        </div>
      </div>
    </div>
  )
}
